import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Home, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PayPalSuccess = () => {
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const orderId = searchParams.get('token') || searchParams.get('order_id');
  const payerId = searchParams.get('PayerID');

  const content = {
    fr: {
      success: 'Paiement PayPal réussi !',
      thanks: 'Merci pour votre achat',
      processing: 'Nous finalisons votre paiement PayPal...',
      confirmationEmail: 'Un email de confirmation a été envoyé à',
      orderNumber: 'Numéro de commande',
      amount: 'Montant payé',
      backHome: 'Retour à l\'accueil',
      contactUs: 'Nous contacter',
      errorTitle: 'Erreur de traitement',
      errorMessage: 'Une erreur est survenue lors du traitement de votre paiement.'
    },
    en: {
      success: 'PayPal payment successful!',
      thanks: 'Thank you for your purchase',
      processing: 'We are finalizing your PayPal payment...',
      confirmationEmail: 'A confirmation email has been sent to',
      orderNumber: 'Order number',
      amount: 'Amount paid',
      backHome: 'Back to home',
      contactUs: 'Contact us',
      errorTitle: 'Processing error',
      errorMessage: 'An error occurred while processing your payment.'
    }
  };

  const t = content[language];

  useEffect(() => {
    if (orderId && payerId) {
      capturePayment();
    } else if (orderId) {
      // Just check status if no payerId
      checkPaymentStatus();
    }
  }, [orderId, payerId]);

  const capturePayment = async () => {
    try {
      // Capture the PayPal payment
      const response = await axios.post(`${API}/payment/paypal/capture/${orderId}`);
      setPaymentStatus(response.data);
    } catch (error) {
      console.error('Error capturing PayPal payment:', error);
      setError(error.response?.data?.detail || 'Payment capture failed');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/payment/paypal/status/${orderId}`);
      setPaymentStatus(response.data);
    } catch (error) {
      console.error('Error checking PayPal payment status:', error);
      setError(error.response?.data?.detail || 'Failed to check payment status');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-12 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-900 mx-auto mb-4" />
            <p className="text-lg text-slate-600">{t.processing}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-2xl">
          <CardContent className="p-12">
            <div className="text-center mb-8">
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-12 w-12 text-red-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">{t.errorTitle}</h1>
              <p className="text-lg text-slate-600">{t.errorMessage}</p>
              <p className="text-sm text-red-600 mt-4">{error}</p>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="bg-blue-900 hover:bg-blue-800">
                <Link to="/">
                  <Home className="h-4 w-4 mr-2" />
                  {t.backHome}
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/contact">
                  {t.contactUs}
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl shadow-2xl">
        <CardContent className="p-12">
          <div className="text-center mb-8">
            <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">{t.success}</h1>
            <p className="text-lg text-slate-600">{t.thanks}</p>
          </div>

          {paymentStatus && (
            <div className="space-y-6 bg-slate-50 p-6 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-600 mb-1">{t.orderNumber}</p>
                  <p className="font-mono text-sm text-slate-900 break-all">
                    {paymentStatus.order_id || orderId}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">{t.amount}</p>
                  <p className="text-2xl font-bold text-green-600">
                    €{paymentStatus.amount || '0.00'}
                  </p>
                </div>
              </div>

              {paymentStatus.payer_email && (
                <div className="pt-4 border-t">
                  <p className="text-sm text-slate-600">
                    {t.confirmationEmail} <span className="font-medium text-slate-900">{paymentStatus.payer_email}</span>
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-blue-900 hover:bg-blue-800">
              <Link to="/">
                <Home className="h-4 w-4 mr-2" />
                {t.backHome}
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/contact">
                {t.contactUs}
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PayPalSuccess;
